// src/components/admin/contas/SubContasVendedores.tsx
// GRAIA Admin — Vendedores: grid de cards roxos + modal Criar/Promover
import React, { useEffect, useState, useCallback, useImperativeHandle } from 'react';
import { Plus, X, ShoppingBag, UserPlus, Search, Ban, CheckCircle, Infinity } from 'lucide-react';
import ConfirmDialog from '../../ConfirmDialog';
import './SubContasVendedores.css';

interface Vendor {
    _id: string;
    username: string;
    email: string;
    vendorConfig: {
        enabled: boolean;
        commissionRate: number;
        commissionBalance: number;
        totalSales: number;
        contractExpiresAt: string | null;
    };
}

type ModalMode = 'create' | 'promote';

const initials = (n: string) => n.slice(0, 2).toUpperCase();
const fmtDate = (d?: string | null) => d ? new Date(d).toLocaleDateString('pt-BR') : '—';
const isExpired = (d?: string | null) => !!d && new Date(d) < new Date();
const inputSt = (disabled = false): React.CSSProperties => ({
    padding: '8px 10px', borderRadius: 7, border: '1px solid var(--sub-border)',
    background: 'var(--sub-surface-2)', color: 'var(--sub-text-primary)',
    fontSize: '0.84rem', outline: 'none', width: '100%', boxSizing: 'border-box',
    opacity: disabled ? 0.4 : 1,
});

const SubContasVendedores = React.forwardRef<{ openModal: (mode: ModalMode) => void }, {}>((_, ref) => {
    const token = localStorage.getItem('accessToken');
    const [vendors, setVendors] = useState<Vendor[]>([]);
    const [loading, setLoading] = useState(true);

    const [showModal, setShowModal] = useState(false);
    const [modalMode, setModalMode] = useState<ModalMode>('create');
    const [saving, setSaving] = useState(false);

    // Criar
    const [form, setForm] = useState({ username: '', email: '', password: '', commissionRate: '30' });
    const [noLimit, setNoLimit] = useState(true);
    const [contractExpiry, setContractExpiry] = useState('');
    const [createError, setCreateError] = useState('');

    // Promover
    const [promoteSearch, setPromoteSearch] = useState('');
    const [promoteResults, setPromoteResults] = useState<{ _id: string; username: string; email: string }[]>([]);
    const [selectedUser, setSelectedUser] = useState<{ _id: string; username: string } | null>(null);
    const [promoteCommission, setPromoteCommission] = useState('30');
    const [promoteNoLimit, setPromoteNoLimit] = useState(true);
    const [promoteExpiry, setPromoteExpiry] = useState('');
    const [promoteError, setPromoteError] = useState('');

    const [confirmOpen, setConfirmOpen] = useState(false);
    const [confirmVendor, setConfirmVendor] = useState<{ id: string; enabled: boolean } | null>(null);

    const fetchVendors = useCallback(async () => {
        setLoading(true);
        try {
            const res = await fetch('/api/admin/accounts/vendors', { headers: { Authorization: `Bearer ${token}` } });
            const data = await res.json();
            if (data.success) setVendors(data.users);
        } finally { setLoading(false); }
    }, [token]);

    useEffect(() => { fetchVendors(); }, [fetchVendors]);

    const openModal = useCallback((mode: ModalMode) => {
        setModalMode(mode); setShowModal(true);
        setForm({ username: '', email: '', password: '', commissionRate: '30' });
        setNoLimit(true); setContractExpiry(''); setCreateError('');
        setPromoteSearch(''); setPromoteResults([]); setSelectedUser(null);
        setPromoteCommission('30'); setPromoteNoLimit(true); setPromoteExpiry(''); setPromoteError('');
    }, []);

    useImperativeHandle(ref, () => ({ openModal }), [openModal]);

    const handleCreate = async () => {
        if (!form.username || !form.email || !form.password) { setCreateError('Preencha todos os campos obrigatórios.'); return; }
        if (!noLimit && !contractExpiry) { setCreateError('Selecione uma data ou marque como Sem limite.'); return; }
        setSaving(true); setCreateError('');
        try {
            const res = await fetch('/api/admin/accounts/create-vendor', {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    ...form,
                    commissionRate: Number(form.commissionRate) / 100,
                    contractExpiresAt: noLimit ? null : contractExpiry
                })
            });
            const data = await res.json();
            if (data.success) { setShowModal(false); fetchVendors(); }
            else setCreateError(data.message || 'Erro ao criar vendedor.');
        } finally { setSaving(false); }
    };

    const searchUsers = useCallback(async (q: string) => {
        if (!q || q.length < 2) { setPromoteResults([]); return; }
        try {
            const res = await fetch(`/api/admin/subscriptions/users?search=${encodeURIComponent(q)}&limit=5`, { headers: { Authorization: `Bearer ${token}` } });
            const data = await res.json();
            if (data.success) setPromoteResults(data.users);
        } catch { }
    }, [token]);

    const handlePromote = async () => {
        if (!selectedUser) return;
        if (!promoteNoLimit && !promoteExpiry) { setPromoteError('Selecione uma data ou marque como Sem limite.'); return; }
        setSaving(true); setPromoteError('');
        try {
            const res = await fetch('/api/admin/promote-to-vendor', {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({ userId: selectedUser!._id, commissionRate: Number(promoteCommission) / 100, contractExpiresAt: promoteNoLimit ? null : promoteExpiry })
            });
            const data = await res.json();
            if (data.success) { setShowModal(false); fetchVendors(); }
            else setPromoteError(data.message || 'Erro ao promover usuário.');
        } finally { setSaving(false); }
    };

    const requestToggle = (id: string, enabled: boolean) => { setConfirmVendor({ id, enabled }); setConfirmOpen(true); };
    const confirmToggle = async () => {
        if (!confirmVendor) return;
        setConfirmOpen(false);
        await fetch(`/api/admin/accounts/${confirmVendor.id}/${confirmVendor.enabled ? 'disable-vendor' : 'enable-vendor'}`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
        setConfirmVendor(null); fetchVendors();
    };

    return (
        <div>
            {loading ? (
                <div className="ven-loading"><div className="ven-spinner" /> Carregando vendedores...</div>
            ) : vendors.length === 0 ? (
                <div className="ven-empty"><div className="ven-empty-icon">🛍️</div>Nenhum vendedor cadastrado.</div>
            ) : (
                <div className="ven-grid">
                    {vendors.map(v => {
                        const expired = isExpired(v.vendorConfig.contractExpiresAt);
                        return (
                            <div key={v._id} className={`ven-card ${v.vendorConfig.enabled ? '' : 'inactive'}`}>
                                <div className="ven-card-top">
                                    <div className="ven-avatar">{initials(v.username)}</div>
                                    <div className="ven-card-info">
                                        <div className="ven-card-name">{v.username}</div>
                                        <div className="ven-card-email">{v.email}</div>
                                    </div>
                                    <span className={`ven-badge ${v.vendorConfig.enabled ? 'active' : 'inactive'}`}>
                                        {v.vendorConfig.enabled ? '● Ativo' : '○ Inativo'}
                                    </span>
                                </div>
                                <div className="ven-card-stats">
                                    <div className="ven-stat">
                                        <div className="ven-stat-value">{(v.vendorConfig.commissionRate * 100).toFixed(0)}%</div>
                                        <div className="ven-stat-label">Comissão</div>
                                    </div>
                                    <div className="ven-stat">
                                        <div className="ven-stat-value">{v.vendorConfig.totalSales}</div>
                                        <div className="ven-stat-label">Vendas</div>
                                    </div>
                                </div>
                                <div className="ven-card-meta">
                                    <span className="ven-meta-label">Contrato</span>
                                    <span className={`ven-meta-value ${!v.vendorConfig.contractExpiresAt ? 'no-limit' : expired ? 'expired' : ''}`}>
                                        {!v.vendorConfig.contractExpiresAt ? '♾ Sem limite' : expired ? `✗ ${fmtDate(v.vendorConfig.contractExpiresAt)}` : fmtDate(v.vendorConfig.contractExpiresAt)}
                                    </span>
                                </div>
                                <div className="ven-card-actions">
                                    <button className={`ven-toggle-btn ${v.vendorConfig.enabled ? 'disable' : 'enable'}`} onClick={() => requestToggle(v._id, v.vendorConfig.enabled)}>
                                        {v.vendorConfig.enabled ? <><Ban size={13} /> Desativar</> : <><CheckCircle size={13} /> Ativar</>}
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {showModal && (
                <div className="ven-overlay" onClick={() => setShowModal(false)}>
                    <div className="ven-modal" onClick={e => e.stopPropagation()}>
                        <div className="ven-modal-header">
                            <h3 className="ven-modal-title"><ShoppingBag size={15} style={{ color: 'var(--ven-purple)' }} /> {modalMode === 'create' ? 'Novo Vendedor' : 'Promover a Vendedor'}</h3>
                            <button className="ven-modal-close" onClick={() => setShowModal(false)}><X size={15} /></button>
                        </div>
                        <div className="ven-modal-body">
                            <div className="ven-mode-tabs">
                                <button className={`ven-mode-tab ${modalMode === 'create' ? 'active' : ''}`} onClick={() => { setModalMode('create'); setCreateError(''); setPromoteError(''); }}><Plus size={13} /> Criar nova conta</button>
                                <button className={`ven-mode-tab ${modalMode === 'promote' ? 'active' : ''}`} onClick={() => { setModalMode('promote'); setCreateError(''); setPromoteError(''); }}><UserPlus size={13} /> Promover existente</button>
                            </div>

                            {modalMode === 'create' && (<>
                                <div className="ven-form-grid">
                                    <div className="ven-form-group"><label>Nome de usuário *</label><input value={form.username} onChange={e => setForm(f => ({ ...f, username: e.target.value }))} placeholder="username" style={inputSt()} /></div>
                                    <div className="ven-form-group"><label>Email *</label><input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} placeholder="email@exemplo.com" style={inputSt()} /></div>
                                    <div className="ven-form-group"><label>Senha *</label><input type="password" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} placeholder="Mínimo 6 caracteres" style={inputSt()} /></div>
                                    <div className="ven-form-group"><label>Comissão (%)</label><input type="number" min="0" max="100" value={form.commissionRate} onChange={e => setForm(f => ({ ...f, commissionRate: e.target.value }))} style={inputSt()} /></div>
                                    <div className="ven-form-group" style={{ gridColumn: '1 / -1' }}><label>Contrato expira em</label><input type="date" value={contractExpiry} onChange={e => setContractExpiry(e.target.value)} disabled={noLimit} style={inputSt(noLimit)} /></div>
                                </div>
                                <label className={`ven-nolimit-check ${noLimit ? 'checked' : ''}`} style={{ marginBottom: 16 }}>
                                    <input type="checkbox" checked={noLimit} onChange={e => { setNoLimit(e.target.checked); if (e.target.checked) setContractExpiry(''); }} />
                                    <Infinity size={14} /> Sem limite de contrato
                                </label>
                                {createError && <p className="ven-error">✗ {createError}</p>}
                                <div className="ven-modal-footer">
                                    <button className="ven-btn secondary" onClick={() => setShowModal(false)}>Cancelar</button>
                                    <button className="ven-btn primary" onClick={handleCreate} disabled={saving || !form.username || !form.email || !form.password}>{saving ? 'Criando...' : <><ShoppingBag size={13} /> Criar Vendedor</>}</button>
                                </div>
                            </>)}

                            {modalMode === 'promote' && (<>
                                <div className="ven-form-group" style={{ marginBottom: 8 }}>
                                    <label>Buscar usuário</label>
                                    <div className="ven-search-wrap">
                                        <Search size={14} />
                                        <input style={{ ...inputSt(), paddingLeft: 32 }} value={promoteSearch} onChange={e => { setPromoteSearch(e.target.value); searchUsers(e.target.value); }} placeholder="Digite nome ou email..." />
                                    </div>
                                </div>
                                {promoteResults.length > 0 && !selectedUser && (
                                    <div className="ven-search-results">
                                        {promoteResults.map(u => (
                                            <button key={u._id} className="ven-search-result" onClick={() => { setSelectedUser(u); setPromoteResults([]); setPromoteSearch(''); }}>
                                                <strong>{u.username}</strong><span>{u.email}</span>
                                            </button>
                                        ))}
                                    </div>
                                )}
                                {selectedUser && (
                                    <div className="ven-selected-chip">
                                        <ShoppingBag size={13} style={{ color: 'var(--ven-purple)', flexShrink: 0 }} />
                                        Selecionado: <strong>{selectedUser.username}</strong>
                                        <button className="ven-chip-clear" onClick={() => { setSelectedUser(null); setPromoteSearch(''); }}><X size={13} /></button>
                                    </div>
                                )}
                                <div className="ven-form-grid">
                                    <div className="ven-form-group"><label>Comissão (%)</label><input type="number" min="0" max="100" value={promoteCommission} onChange={e => setPromoteCommission(e.target.value)} style={inputSt()} /></div>
                                    <div className="ven-form-group"><label>Contrato expira em</label><input type="date" value={promoteExpiry} onChange={e => setPromoteExpiry(e.target.value)} disabled={promoteNoLimit} style={inputSt(promoteNoLimit)} /></div>
                                </div>
                                <label className={`ven-nolimit-check ${promoteNoLimit ? 'checked' : ''}`} style={{ marginBottom: 16 }}>
                                    <input type="checkbox" checked={promoteNoLimit} onChange={e => { setPromoteNoLimit(e.target.checked); if (e.target.checked) setPromoteExpiry(''); }} />
                                    <Infinity size={14} /> Sem limite de contrato
                                </label>
                                {promoteError && <p className="ven-error">✗ {promoteError}</p>}
                                <div className="ven-modal-footer">
                                    <button className="ven-btn secondary" onClick={() => setShowModal(false)}>Cancelar</button>
                                    <button className="ven-btn primary" onClick={handlePromote} disabled={!selectedUser || saving}>{saving ? 'Promovendo...' : <><ShoppingBag size={13} /> Tornar Vendedor</>}</button>
                                </div>
                            </>)}
                        </div>
                    </div>
                </div>
            )}

            <ConfirmDialog
                isOpen={confirmOpen}
                title={confirmVendor?.enabled ? 'Desativar Vendedor' : 'Ativar Vendedor'}
                message={confirmVendor?.enabled ? 'Deseja desativar este vendedor?' : 'Deseja reativar este vendedor?'}
                confirmText={confirmVendor?.enabled ? 'Desativar' : 'Ativar'}
                cancelText="Cancelar"
                onConfirm={confirmToggle}
                onCancel={() => { setConfirmOpen(false); setConfirmVendor(null); }}
            />
        </div>
    );
});

SubContasVendedores.displayName = 'SubContasVendedores';
export default SubContasVendedores;